import React, { useState, useEffect } from "react";
import { testimonials as mockTestimonials } from "../mock";
import { ChevronLeft, ChevronRight, Quote, Star, PenSquare } from "lucide-react";
import TestimonialTile from "./testimonials/TestimonialTile";
import ReviewForm from "./testimonials/ReviewForm";
import { fetchApprovedReviews } from "../api";

const PER_PAGE = 8;

function normalizeReview(r) {
  return {
    id: r.id || `srv-${r._id || Math.random()}`,
    author: r.author,
    role: r.location || "Verified customer",
    rating: r.rating || 5,
    text: r.text,
  };
}

function FeaturedTestimonial({ testimonial, currentIdx, total, onPrev, onNext }) {
  const rating = testimonial.rating || 5;
  return (
    <div className="mt-14 relative bg-white border border-neutral-200 p-8 md:p-12 shadow-sm">
      <Quote className="text-neutral-300 mb-4" size={40} />
      <div className="flex items-center gap-1 mb-4">
        {[...Array(rating)].map((_, i) => (
          <Star key={`f-star-${i}`} size={16} className="fill-neutral-900 text-neutral-900" />
        ))}
      </div>
      <p className="text-neutral-800 text-lg md:text-xl leading-relaxed min-h-[200px]">
        &ldquo;{testimonial.text}&rdquo;
      </p>
      <div className="mt-8 flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <div className="font-display text-lg text-neutral-900">{testimonial.author}</div>
          <div className="text-xs tracking-[0.3em] text-neutral-500 mt-1">{testimonial.role}</div>
        </div>
        <div className="flex items-center gap-3">
          <button
            onClick={onPrev}
            className="w-11 h-11 border border-neutral-900 flex items-center justify-center hover:bg-neutral-900 hover:text-[#fbf3e4] transition-colors"
            aria-label="previous"
          >
            <ChevronLeft size={18} />
          </button>
          <div className="flex gap-2 items-center">
            <span className="font-display text-sm text-neutral-900">{currentIdx + 1}</span>
            <span className="text-neutral-400 text-sm">/</span>
            <span className="text-neutral-500 text-sm">{total}</span>
          </div>
          <button
            onClick={onNext}
            className="w-11 h-11 border border-neutral-900 flex items-center justify-center hover:bg-neutral-900 hover:text-[#fbf3e4] transition-colors"
            aria-label="next"
          >
            <ChevronRight size={18} />
          </button>
        </div>
      </div>
    </div>
  );
}

function Pagination({ page, totalPages, onPrev, onNext, onSelect }) {
  if (totalPages <= 1) return null;
  return (
    <div className="flex items-center justify-center gap-4 mt-8">
      <button
        onClick={onPrev}
        className="w-9 h-9 border border-neutral-900 flex items-center justify-center hover:bg-neutral-900 hover:text-[#fbf3e4] transition-colors"
        aria-label="prev page"
      >
        <ChevronLeft size={16} />
      </button>
      <div className="flex gap-2">
        {[...Array(totalPages)].map((_, p) => (
          <button
            key={`pg-${p}`}
            onClick={() => onSelect(p)}
            className={`w-2.5 h-2.5 rounded-full transition-colors ${p === page ? "bg-neutral-900" : "bg-neutral-400"}`}
            aria-label={`page ${p + 1}`}
          />
        ))}
      </div>
      <button
        onClick={onNext}
        className="w-9 h-9 border border-neutral-900 flex items-center justify-center hover:bg-neutral-900 hover:text-[#fbf3e4] transition-colors"
        aria-label="next page"
      >
        <ChevronRight size={16} />
      </button>
    </div>
  );
}

export default function Testimonials() {
  const [idx, setIdx] = useState(0);
  const [page, setPage] = useState(0);
  const [reviews, setReviews] = useState(mockTestimonials);
  const [hasReal, setHasReal] = useState(false);
  const [formOpen, setFormOpen] = useState(false);

  const loadReviews = async () => {
    try {
      const remote = await fetchApprovedReviews();
      if (Array.isArray(remote) && remote.length > 0) {
        setReviews(remote.map(normalizeReview));
        setHasReal(true);
        setIdx(0);
        setPage(0);
      }
    } catch (err) {
      // silently fall back to mock testimonials
    }
  };

  useEffect(() => {
    loadReviews();
  }, []);

  const total = reviews.length;
  const totalPages = Math.max(1, Math.ceil(total / PER_PAGE));
  const pageItems = reviews.slice(page * PER_PAGE, page * PER_PAGE + PER_PAGE);
  const current = reviews[idx] || reviews[0];

  const avgRating = (
    reviews.reduce((s, r) => s + (r.rating || 5), 0) / Math.max(1, reviews.length)
  ).toFixed(1);

  return (
    <section id="testimonials" className="bg-[#f3ead6] py-24 lg:py-32">
      <div className="max-w-6xl mx-auto px-6 lg:px-10">
        <div className="text-center max-w-2xl mx-auto">
          <p className="text-xs tracking-[0.4em] text-neutral-500 mb-4">TESTIMONIALS</p>
          <h3 className="font-display text-4xl md:text-6xl leading-[1] text-neutral-900">
            What clients say
          </h3>
          <div className="flex flex-wrap items-center justify-center gap-2 mt-6">
            {[...Array(5)].map((_, i) => (
              <Star key={`avg-star-${i}`} size={18} className="fill-neutral-900 text-neutral-900" />
            ))}
            <span className="ml-2 text-sm text-neutral-700">
              {avgRating} / 5 average from {total} {hasReal ? "verified" : "sample"} reviews
            </span>
          </div>

          <button
            type="button"
            onClick={() => setFormOpen(true)}
            className="btn-primary mt-8 inline-flex items-center gap-2 bg-neutral-900 text-[#fbf3e4] px-6 py-3 text-sm font-medium tracking-wide hover:bg-neutral-700"
          >
            <PenSquare size={16} /> Write a review
          </button>
        </div>

        {current && (
          <FeaturedTestimonial
            testimonial={current}
            currentIdx={idx}
            total={total}
            onPrev={() => setIdx((i) => (i - 1 + total) % total)}
            onNext={() => setIdx((i) => (i + 1) % total)}
          />
        )}

        <div className="mt-10">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {pageItems.map((r) => {
              const globalIdx = reviews.findIndex((t) => t.id === r.id);
              return (
                <TestimonialTile
                  key={r.id}
                  testimonial={r}
                  active={globalIdx === idx}
                  onSelect={() => setIdx(globalIdx)}
                />
              );
            })}
          </div>

          <Pagination
            page={page}
            totalPages={totalPages}
            onPrev={() => setPage((p) => (p - 1 + totalPages) % totalPages)}
            onNext={() => setPage((p) => (p + 1) % totalPages)}
            onSelect={setPage}
          />
        </div>
      </div>

      <ReviewForm open={formOpen} onClose={() => setFormOpen(false)} onSubmitted={loadReviews} />
    </section>
  );
}
